// Basit AppContext - hata olmadan çalışacak
import React, { createContext, useContext, ReactNode } from 'react';
import { Firma } from '../types';
import { AppProviderV2, useAppContextV2 } from './AppContextV2';
import { useAuth } from './AuthContext';
import { useFirmaOptions } from '../hooks/useFirmaOptions';

interface AppContextType {
  selectedFirma: Firma | null;
  setSelectedFirma: (firma: Firma | null) => void;
  selectedYil: number;
  setSelectedYil: (yil: number) => void;
  userFirmaOptions: Firma[];
  setUserFirmaOptions: React.Dispatch<React.SetStateAction<Firma[]>>;
  isLoading: boolean;
  requiresSelection: boolean;
  canProceed: boolean;
  isReady: boolean;
  renderGuard: () => React.ReactElement | null;
  authState: string;
  authError: string | null;
  retry: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

const AppContextWrapper: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { profile } = useAuth();
  const v2Context = useAppContextV2();
  const { firmaOptions, isLoading: firmaLoading } = useFirmaOptions();
  const [lastFirmaCount, setLastFirmaCount] = React.useState(0);
  const hasAppliedSuperAdminDefaults = React.useRef(false);

  // Sadece firma sayısı değiştiğinde log
  React.useEffect(() => {
    const currentCount = firmaOptions?.length || 0;
    if (currentCount !== lastFirmaCount) {
      // console.log('🔍 AppContext: firmaOptions.length:', currentCount);
      setLastFirmaCount(currentCount);
    }
  }, [firmaOptions?.length, lastFirmaCount]);
  
  // AuthContext ile senkronize firma seçimi - İyileştirilmiş
  const [hasInitialized, setHasInitialized] = React.useState(false);
  
  React.useEffect(() => {
    if (hasInitialized || !firmaOptions || firmaOptions.length === 0) return;

    const persistentSession = localStorage.getItem('persistent_session');
    const isSuperAdmin = profile?.rol === 'ekostrateji_super_admin';
    const normalizeFirmaName = (value?: string | null) => (value || '').trim().toLocaleLowerCase('tr-TR');
    const defaultSuperAdminFirmaName = normalizeFirmaName('Ekostrateji Holding');
    const targetYear = 2025;

    let targetFirma: Firma | null = null;

    if (isSuperAdmin) {
      targetFirma = firmaOptions.find(f =>
        normalizeFirmaName(f.firma_adi) === defaultSuperAdminFirmaName ||
        normalizeFirmaName(f.label) === defaultSuperAdminFirmaName ||
        normalizeFirmaName(f.value) === defaultSuperAdminFirmaName
      ) || firmaOptions[0] || null;

      if (targetFirma) {
        if (!v2Context.selectedFirma || v2Context.selectedFirma.id !== targetFirma.id) {
          v2Context.setSelectedFirma(targetFirma);
        }

        if (v2Context.selectedYil !== targetYear) {
          v2Context.setSelectedYil(targetYear);
        }

        if (persistentSession) {
          try {
            const currentSession = JSON.parse(persistentSession);
            localStorage.setItem('persistent_session', JSON.stringify({
              ...currentSession,
              selectedFirma: {
                id: targetFirma.id,
                firma_adi: targetFirma.firma_adi || targetFirma.label,
                label: targetFirma.label
              },
              timestamp: Date.now()
            }));
          } catch (error) {
            console.warn('Persistent session güncellenemedi:', error);
          }
        }

        hasAppliedSuperAdminDefaults.current = true;
        setHasInitialized(true);
        return;
      }
    }

    if (v2Context.selectedFirma) {
      setHasInitialized(true);
      return;
    }

    // Persistent session'dan firma bilgisini al
    let target = null;

    if (persistentSession) {
      try {
        const sessionData = JSON.parse(persistentSession);
        if (sessionData.selectedFirma) {
          // Persistent session'daki firmayı bul - ID karşılaştırmasını iyileştir
          target = firmaOptions.find(f =>
            f.id === sessionData.selectedFirma.id ||
            f.firma_adi === sessionData.selectedFirma.firma_adi
          );
        }
      } catch (error) {
        console.warn('Persistent session parse hatası:', error);
      }
    }

    // Eğer persistent session'da firma yoksa ilk firmayı seç
    if (!target) {
      target = firmaOptions[0];
    }

    if (target) {
      v2Context.setSelectedFirma(target);
      
      // Persistent session'ı güncelle
      const currentSession = persistentSession ? JSON.parse(persistentSession) : {};
      localStorage.setItem('persistent_session', JSON.stringify({
        ...currentSession,
        selectedFirma: {
          id: target.id,
          firma_adi: target.firma_adi || target.label,
          label: target.label
        },
        timestamp: Date.now()
      }));
      
      // Tüm modül cache'lerini temizle
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('moduleState_')) {
          localStorage.removeItem(key);
        }
      });
    }

    setHasInitialized(true);
  }, [firmaOptions?.length, hasInitialized, profile?.rol, v2Context.selectedFirma, v2Context.selectedYil, v2Context.setSelectedFirma, v2Context.setSelectedYil]);
  
  const value: AppContextType = {
    selectedFirma: v2Context.selectedFirma,
    setSelectedFirma: v2Context.setSelectedFirma,
    selectedYil: v2Context.selectedYil,
    setSelectedYil: v2Context.setSelectedYil,
    userFirmaOptions: firmaOptions || [],
    setUserFirmaOptions: () => {},
    isLoading: firmaLoading,
    requiresSelection: false,
    canProceed: true,
    isReady: true,
    renderGuard: () => null,
    authState: 'ready',
    authError: null,
    retry: () => {}
  };
  
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <AppProviderV2>
      <AppContextWrapper>{children}</AppContextWrapper>
    </AppProviderV2>
  );
};

export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) throw new Error('useAppContext must be used within an AppProvider');
  return context;
};
